import { isExampleResource } from '@gonfalon/analytics/isExampleResource';
import { useGuidanceStepProps, useSaveGuidanceSteps } from '@gonfalon/onboarding-guidance/GuidanceInitialValidContext';
import { useRollbackId } from '@gonfalon/rollback/useRollbackContext';
import {
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuItem,
  MenuTrigger,
  Perceivable,
  Popover,
  Separator,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { useStore } from '@tanstack/react-form';
import { addHours, isBefore, startOfHour } from 'date-fns';
import { isEmpty } from 'lodash';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import type { ZodIssue } from 'zod';

import { timedFlagTargetingEvents, trackFlagTargetingEvent } from '#utils/flags/flagAnalytics';
import { SaveOptions } from '#utils/saveButtonUtils';
import { trackScheduleFlagChangesEvent } from '#utils/scheduledChanges/trackScheduleFlagChangesEvent';

import type { PendingChangesFormType } from './usePendingChangesFormAdapter';

import styles from './PendingChangesModal.module.css';

export interface SaveButtonProps {
  saveOption: SaveOptions;
  flagKey?: string;
  children: string;
  hideScheduleOption?: boolean;
  form: PendingChangesFormType;
  isCustomScheduleSelected: boolean;
  setIsCustomScheduleSelected: (isCustomScheduleSelected: boolean) => void;
  setShowOptionalReviewers: (showOptionalReviewers: boolean) => void;
  showBypassRequiredApprovals: boolean;
}

export type MenuItemType =
  | {
      id: string;
      label: string;
      timestamp?: number;
      type: 'item';
    }
  | {
      type: 'divider';
    };

export const getScheduleMenuItems = (): MenuItemType[] => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  const laterToday = new Date(year, month, day, 15, 0, 0).getTime();
  const tomorrow = new Date(year, month, day + 1, 9, 0, 0);
  const monday = new Date(year, month, day + 3, 9, 0, 0); // Friday + 3 days = Monday
  const menuItems: MenuItemType[] = [];
  const canScheduledToday = isBefore(now, laterToday);
  // if now is before 3PM, add an option to schedule for today
  if (canScheduledToday) {
    menuItems.push({
      id: 'today',
      label: 'Schedule for today at 3PM',
      timestamp: laterToday,
      type: 'item',
    });
  }

  if (now.getDay() !== 5) {
    menuItems.push({
      id: 'next-business-day',
      label: 'Schedule for tomorrow at 9AM',
      timestamp: tomorrow.getTime(),
      type: 'item',
    });
  } else {
    // if today is Friday, add an option to schedule for Monday at 9AM

    menuItems.push({
      id: 'next-business-day',
      label: 'Schedule for Monday at 9AM',
      timestamp: monday.getTime(),
      type: 'item',
    });
  }

  menuItems.push({
    id: 'custom',
    label: 'Schedule a custom date and time',
    type: 'item',
  });

  return menuItems;
};

// We can probably use a feature flag to customize the options
export const getMenuItems = (hideScheduleOption: boolean, showBypassRequiredApprovals: boolean): MenuItemType[] => {
  const menuItems = hideScheduleOption ? [] : getScheduleMenuItems();
  if (showBypassRequiredApprovals) {
    if (!hideScheduleOption) {
      menuItems.push({ type: 'divider' });
    }

    menuItems.push({
      id: 'bypass-required-approvals',
      label: 'Save without approval',
      type: 'item',
    });
  }

  return menuItems;
};

export type FormErrors = Record<string, Record<string, ZodIssue[]>>;

export const getTooltipContent = (formErrors: FormErrors): ZodIssue[] => {
  const { onChange, onSubmit } = formErrors;

  if (!isEmpty(onChange)) {
    return Object.values(onChange).flatMap((zodIssue) => zodIssue[0]);
  }
  if (!isEmpty(onSubmit)) {
    return Object.values(onSubmit).flatMap((zodIssue) => zodIssue[0]);
  }
  return [];
};

interface WrapperProps {
  canSubmit: boolean;
  tooltipContent: ZodIssue[];
  children: React.ReactNode;
}

function Wrapper({ canSubmit, tooltipContent, children }: WrapperProps) {
  return (
    <Perceivable isDisabled={!canSubmit}>
      <TooltipTrigger isDisabled={tooltipContent.length === 0}>
        {children}

        <Tooltip>
          <ul aria-label="Form errors" className={styles.errorList}>
            {tooltipContent.map((error) => (
              <li key={`${error.code}-${error.path.join('.')}`}>{error.message}</li>
            ))}
          </ul>
        </Tooltip>
      </TooltipTrigger>
    </Perceivable>
  );
}

interface SubmitButtonProps {
  handleSubmit: () => Promise<void>;
  saveOption: SaveOptions;
  flagKey: string | undefined;
  children: string;
  isPending: boolean;
}

function SubmitButton({ handleSubmit, isPending, saveOption, flagKey, children }: SubmitButtonProps) {
  const { confirmationGuidanceStep } = useSaveGuidanceSteps();
  const guidanceStepProps = useGuidanceStepProps(confirmationGuidanceStep);
  const rollbackId = useRollbackId();
  const isDestructive = saveOption === SaveOptions.DELETE && children === 'Delete';

  return (
    <Button
      type="submit"
      isPending={isPending}
      {...guidanceStepProps}
      variant={isDestructive ? 'destructive' : 'primary'}
      data-test-id="submit-pending-changes-btn"
      onPress={(e) => {
        // Set validation so guidance popover sees it before modal closes (handleDone runs, then submit)
        const el = (e.target as HTMLElement)?.closest?.('[data-guidance-step]');
        if (el) {
          el.setAttribute('data-guidance-valid', 'true');
        }

        trackFlagTargetingEvent
          .measure(timedFlagTargetingEvents.TIME_TO_SAVE_FLAG_CONFIGURATION)
          .track('Review And Save Submit Button Clicked', {
            saveOption,
            flagKey,
            cta: children,
            rollbackId,
            isExampleResource: isExampleResource(flagKey),
          })
          .restart();

        trackFlagTargetingEvent.measureAndTrack(timedFlagTargetingEvents.TIME_ON_REVIEW_AND_SAVE, {
          type: 'submit',
          saveAction: children,
          rollbackId,
        });

        void handleSubmit();
      }}
    >
      {children}
    </Button>
  );
}

export function SaveButton({
  saveOption,
  flagKey,
  children,
  hideScheduleOption = false,
  form,
  isCustomScheduleSelected,
  setIsCustomScheduleSelected,
  showBypassRequiredApprovals,
  setShowOptionalReviewers,
}: SaveButtonProps) {
  const rollbackId = useRollbackId();
  const { canSubmit, tooltipContent, canPerformSecondary } = useStore(form.store, (state) => {
    const formErrors = state.errorMap as FormErrors;
    const zodIssues = getTooltipContent(formErrors);

    const performSecondary = showBypassRequiredApprovals ? true : state.canSubmit;

    return {
      canSubmit: zodIssues.length === 0,
      tooltipContent: zodIssues,
      canPerformSecondary: performSecondary,
    };
  });

  const menuItems = getMenuItems(hideScheduleOption, showBypassRequiredApprovals);

  if (menuItems.length === 0 || isCustomScheduleSelected) {
    return (
      <Wrapper canSubmit={canSubmit} tooltipContent={tooltipContent}>
        <SubmitButton
          handleSubmit={form.handleSubmit}
          isPending={form.state.isSubmitting}
          saveOption={saveOption}
          flagKey={flagKey}
        >
          {children}
        </SubmitButton>
      </Wrapper>
    );
  }

  return (
    <ButtonGroup spacing="compact">
      <Wrapper canSubmit={canSubmit} tooltipContent={tooltipContent}>
        <SubmitButton
          handleSubmit={form.handleSubmit}
          isPending={form.state.isSubmitting}
          saveOption={saveOption}
          flagKey={flagKey}
        >
          {children}
        </SubmitButton>
      </Wrapper>
      <form.AppField name="isScheduleSelected">
        {(field) => {
          const { isTouched, isDirty, errors } = field.getMeta();

          return (
            <MenuTrigger>
              <IconButton
                variant={
                  (isTouched && isDirty && errors.length > 0) || saveOption === SaveOptions.DELETE
                    ? 'destructive'
                    : 'primary'
                }
                data-test-id="save-options-dropdown-btn"
                aria-label="Schedule changes"
                icon="chevron-down"
                isDisabled={!canPerformSecondary}
              />
              <Popover placement="bottom end">
                <Menu aria-label="Schedule changes menu">
                  {menuItems.map((item) =>
                    item.type === 'divider' ? (
                      <Separator key="separator" />
                    ) : (
                      <MenuItem
                        key={item.id}
                        id={item.id}
                        onAction={async () => {
                          const { timestamp } = item;

                          if (item.id === 'bypass-required-approvals') {
                            form.setFieldValue('bypassRequiredApprovals', true);
                            setShowOptionalReviewers(false);
                            form.setFieldValue('reviewers', []);
                            const changeErrors = await form.validate('change');

                            if (Object.keys(changeErrors).length === 0) {
                              trackFlagTargetingEvent
                                .measure(timedFlagTargetingEvents.TIME_TO_SAVE_FLAG_CONFIGURATION)
                                .track('Review And Save Submit Button Clicked', {
                                  saveOption,
                                  flagKey,
                                  cta: item.label,
                                  rollbackId,
                                  isExampleResource: isExampleResource(flagKey),
                                })
                                .restart();

                              trackFlagTargetingEvent.measureAndTrack(
                                timedFlagTargetingEvents.TIME_ON_REVIEW_AND_SAVE,
                                {
                                  type: 'submit',
                                  saveAction: children,
                                  cta: item.label,
                                  rollbackId,
                                },
                              );

                              void form.handleSubmit();
                            }
                          } else if (item.id === 'custom') {
                            setIsCustomScheduleSelected(true);
                            const defaultDate = startOfHour(addHours(new Date(), 1));
                            form.setFieldValue('executionDate', defaultDate.getTime());
                            trackScheduleFlagChangesEvent('Schedule Changes Dropdown Button Clicked', {
                              cta: item.label,
                            });
                            field.handleChange(true);
                          } else if (typeof timestamp === 'number') {
                            form.setFieldValue('executionDate', timestamp);
                            field.handleChange(true);
                            setIsCustomScheduleSelected(false);

                            trackFlagTargetingEvent
                              .measure(timedFlagTargetingEvents.TIME_TO_SAVE_FLAG_CONFIGURATION)
                              .track('Schedule Changes Dropdown Button Clicked', {
                                cta: item.label,
                                rollbackId,
                              })
                              .restart();

                            trackFlagTargetingEvent.measureAndTrack(timedFlagTargetingEvents.TIME_ON_REVIEW_AND_SAVE, {
                              type: 'submit',
                              cta: item.label,
                              saveOption: children,
                              rollbackId,
                            });

                            void form.handleSubmit();
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    ),
                  )}
                </Menu>
              </Popover>
            </MenuTrigger>
          );
        }}
      </form.AppField>
    </ButtonGroup>
  );
}
